// Libraries
import isEmpty from 'lodash/isEmpty'

// Shared
import { featureDisabled, FeatureFlags } from 'back_office/v2/utils/utils'

// Build an array of arrays, where each subarray is a row of field names
const buildFieldNamesArray = (formLayout, showFirstAndLastName = true) => {
  const fieldsArray = []

  if (isEmpty(formLayout)) return fieldsArray

  const rows = formLayout.split('\n')

  rows.forEach((row) => {
    const fields = row.match(/({\w+})/g)

    if (!fields) return

    // Remove curled braces from the field names
    let fieldNames = fields.map((field) => {
      return field.replace(/[{}]/g, '')
    })

    if (!showFirstAndLastName) {
      fieldNames = fieldNames.filter((fieldName) => {
        return !['first_name', 'last_name'].includes(fieldName)
      })
    }

    fieldsArray.push(fieldNames)
  })

  return fieldsArray
}

const buildFieldLabel = ({ name, country, translationPrefix }) => {
  const prefix = translationPrefix || 'common'

  switch (name) {
    case 'first_name':
    case 'last_name':
      return `${prefix}.${name}`
    case 'address1':
      return `${prefix}.address_fields.address_line_1`
    case 'address2':
      return `${prefix}.address_fields.address_line_2`
    case 'city':
    case 'country':
      return `${prefix}.address_fields.${name}`
    case 'province': {
      if (country?.province_type) {
        return `${prefix}.address_fields.province_type.${country.province_type}`
      } else {
        return `${prefix}.address_fields.province`
      }
    }
    case 'zipcode': {
      if (country?.zipcode_type) {
        return `${prefix}.address_fields.zipcode_type.${country.zipcode_type}`
      } else {
        return `${prefix}.address_fields.zipcode`
      }
    }
  }
}

const buildFieldPlaceholder = ({ name, country, translationPrefix }) => {
  const prefix = translationPrefix || 'common'

  switch (name) {
    case 'first_name':
    case 'last_name':
      return `${prefix}.${name}`
    case 'address1':
      return `${prefix}.address_fields.address_line_1_placeholder`
    case 'address2':
      return `${prefix}.address_fields.address_line_2_placeholder`
    case 'city':
      return `${prefix}.address_fields.${name}`
    case 'province': {
      if (country?.province_type) {
        return `${prefix}.address_fields.province_type.${country.province_type}`
      } else {
        return `${prefix}.address_fields.province`
      }
    }
    case 'zipcode': {
      if (country?.zipcode_type) {
        return `${prefix}.address_fields.zipcode_type.${country.zipcode_type}`
      } else {
        return `${prefix}.address_fields.zipcode`
      }
    }
  }
}

const buildFieldError = ({ name, country, t, errorKey, translationPrefix }) => {
  const prefix = translationPrefix || 'common'

  let fieldLabel = ''

  switch (name) {
    case 'first_name':
    case 'last_name':
      fieldLabel = t(`${prefix}.${name}`)
      break
    case 'address1':
      fieldLabel = t(`${prefix}.address_fields.address_line_1`)
      break
    case 'address2':
      fieldLabel = t(`${prefix}.address_fields.address_line_2`)
      break
    case 'city':
    case 'country':
      fieldLabel = t(`${prefix}.address_fields.${name}`)
      break
    case 'province': {
      if (country?.province_type) {
        fieldLabel = t(`${prefix}.address_fields.province_type.${country.province_type}`)
      } else {
        fieldLabel = t(`${prefix}.address_fields.province`)
      }
      break
    }
    case 'zipcode': {
      if (country?.zipcode_type) {
        fieldLabel = t(`${prefix}.address_fields.zipcode_type.${country.zipcode_type}`)
      } else {
        fieldLabel = t(`${prefix}.address_fields.zipcode`)
      }
      break
    }
  }

  return `${fieldLabel} ${t(errorKey || 'common.errors.mixed.default')}`
}

// Check if any of the address field values have been changed.
// It ignores undefined values and the country if it's the default country.
const addressDirty = (values, initialValues, addressIdentifier, defaultCountryId) => {
  values = extractValuesForIdentifier(values, addressIdentifier)
  initialValues = extractValuesForIdentifier(initialValues, addressIdentifier)

  if (isEmpty(values)) return false

  const countryHasChanged = presence(values.country_id) !== presence(initialValues?.country_id) &&
    values.country_id !== defaultCountryId

  if (countryHasChanged) return true

  const fields = ['first_name', 'last_name', 'address1', 'address2', 'city', 'zipcode', 'province_id']

  const someFieldHasChanged = fields.some((field) => {
    return presence(values[field]) !== presence(initialValues?.[field])
  })

  if (someFieldHasChanged) return true

  const provinceHasBeenSelected = values.region === values.province_id
  const regionHasChanged = presence(values.region) !== presence(initialValues?.region) && !provinceHasBeenSelected

  return regionHasChanged
}

// Whether the address needs to be persisted.
const shouldPersistAddressAttributes = (values) => {
  if (featureDisabled(FeatureFlags.NEW_ADDRESS_VALIDATION)) return true

  if (!addressEmpty(values)) return true

  const state = window.store.getState()
  const companyId = state.session.currentCompanyId
  const company = state.orm.companies.getById(companyId)
  const defaultCountryId = state.orm.countries.getFirst({ name: company.country })?.id

  const isDefaultCountry = values.country_id === defaultCountryId
  const isEmptyCountry = isEmpty(values.country_id)

  return !isEmptyCountry && !isDefaultCountry
}

// Check if all the address fields are empty, except for the country_id
const addressEmpty = (values) => {
  // country_id === country when we set the initial value of the country_id with the default country
  if (!isEmpty(values.country_id) && !isEmpty(values.country) && values.country_id !== values.country) {
    return false
  }

  const fields = [
    'first_name', 'last_name', 'address1', 'address2', 'city', 'zipcode', 'province_id', 'region'
  ]

  return fields.every((field) => {
    return isEmpty(values[field])
  })
}

const extractValuesForIdentifier = (values, addressIdentifier) => {
  if (isEmpty(addressIdentifier)) {
    return values
  } else {
    // Sometimes, the address identifier is a string like `attributes[0]`
    const regexp = /^(?<identifier>\w+)\[(?<index>\d+)\]$/
    const match = addressIdentifier.match(regexp)
    if (match) {
      const { identifier, index } = match.groups

      return values[identifier][index]
    } else {
      return values[addressIdentifier]
    }
  }
}

const setValueForIdentifier = (fieldName, value, initialValues, addressIdentifier) => {
  if (isEmpty(addressIdentifier)) {
    initialValues[fieldName] = value
  } else {
    // Sometimes, the address identifier is a string like `attributes[0]`
    const regexp = /^(?<identifier>\w+)\[(?<index>\d+)\]$/
    const match = addressIdentifier.match(regexp)
    if (match) {
      const { identifier, index } = match.groups

      if (initialValues[identifier] && initialValues[identifier][index]) {
        initialValues[identifier][index][fieldName] = value
      }
    } else {
      if (initialValues[addressIdentifier]) {
        initialValues[addressIdentifier][fieldName] = value
      }
    }
  }
}

// Return the value if it's not empty (for strings/null/undefined), otherwise return null.
const presence = (string) => {
  return string || null
}

export {
  buildFieldNamesArray,
  buildFieldLabel,
  buildFieldPlaceholder,
  buildFieldError,
  addressDirty,
  shouldPersistAddressAttributes,
  addressEmpty,
  extractValuesForIdentifier,
  setValueForIdentifier
}
